import { FileSpreadsheet, Info, Loader2, Settings2 } from "lucide-react";
import type { Branding } from "../../lib/branding";

const card =
  "flex w-56 flex-col gap-1.5 rounded-md border border-line bg-canvas/40 p-3";
const infoBox =
  "flex w-64 items-start gap-2 rounded-md border border-line bg-canvas/20 p-2.5 text-[11px] leading-snug text-muted";

function RogoDefaultCard(props: {
  selectedCount: number;
  busy: boolean;
  rogoBlocked: boolean;
  unpriced: string[];
  onDownload: () => void;
}) {
  const { selectedCount, busy, rogoBlocked, unpriced, onDownload } = props;
  return (
    <div className="flex items-stretch gap-3">
      <div className={`${card} ${rogoBlocked ? "opacity-60" : ""}`}>
        <span className="text-[12px] font-semibold text-fg">
          ROGO Default Report
        </span>
        <span className="text-[11px] leading-snug text-muted">
          The statement letter + workbook exactly as standard, on ROGO
          letterhead.
        </span>
        {rogoBlocked && (
          <span className="text-[10px] leading-snug text-amber-500">
            Unavailable — ROGO has no published MAP/DAP and potash market for{" "}
            {unpriced
              .map((st) => (st === "NA" ? "fields outside the 50 states" : st))
              .join(", ")}
            . Use the Branded Report and enter your own values.
          </span>
        )}
        <button
          disabled={selectedCount === 0 || busy || rogoBlocked}
          onClick={onDownload}
          className="mt-auto flex items-center justify-center gap-1.5 rounded border border-accent bg-accent px-2 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={13} />
          )}{" "}
          {rogoBlocked ? "Unavailable" : "Download"}
        </button>
      </div>
      <div className={infoBox}>
        <Info size={13} className="mt-0.5 shrink-0 text-accent" />
        <span>
          Uses a methodology ROGO can definitively stand behind for depreciating
          P &amp; K, priced on each field&rsquo;s own state market.
        </span>
      </div>
    </div>
  );
}

function BrandedReportCard(props: {
  selectedCount: number;
  busy: boolean;
  branding: Branding | null;
  brandingLoaded: boolean;
  showCustomize: boolean;
  customizeDisabled: boolean;
  onDownload: () => void;
  onCustomize: () => void;
}) {
  const {
    selectedCount,
    busy,
    branding,
    brandingLoaded,
    showCustomize,
    customizeDisabled,
    onDownload,
    onCustomize,
  } = props;
  return (
    <div className="flex items-stretch gap-3">
      <div
        className={`${card} relative isolate pb-10 ${branding == null ? "opacity-60" : ""}`}
      >
        <span className="text-[12px] font-semibold text-fg">
          Branded Report
        </span>
        <span className="text-[11px] leading-snug text-muted">
          The same report with{" "}
          {branding ? `${branding.name} branding` : "your logo and address"} on
          the letter.
        </span>
        {brandingLoaded && branding == null && (
          <span className="text-[10px] leading-snug text-amber-500">
            No branding set up yet — add it under “Add Branding” in the profile
            menu.
          </span>
        )}
        <button
          disabled={selectedCount === 0 || busy || branding == null}
          onClick={onDownload}
          className="mt-auto flex items-center justify-center gap-1.5 rounded border border-accent bg-accent px-2 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={13} />
          )}{" "}
          Download
        </button>
        {/* BRANDED ONLY: the ROGO Default has none, protecting the methodology ROGO stands behind. */}
        {showCustomize && (
          <button
            disabled={customizeDisabled}
            className="absolute bottom-2 right-2 z-scrim flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas shadow hover:bg-accent-hover disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              onCustomize();
            }}
            title="Edit the thresholds and market values before downloading"
          >
            <Settings2 size={11} /> Customize
          </button>
        )}
      </div>
      <div className={infoBox}>
        <Info size={13} className="mt-0.5 shrink-0 text-accent" />
        <span>
          Adds your own logo and letterhead. Customizing will soon let you add
          additional nutrients, or build reports for fields filed in the last 7
          years.
        </span>
      </div>
    </div>
  );
}

// The two report presets. The ROGO card has no Customize control: that path is the
// methodology ROGO stands behind, and a price edit belongs only on the branded card.
export function Section180ReportChoices(props: {
  selectedCount: number;
  busy: boolean;
  rogoBlocked: boolean;
  unpriced: string[];
  branding: Branding | null;
  brandingLoaded: boolean;
  showCustomize: boolean;
  customizeDisabled: boolean;
  onRogo: () => void;
  onBranded: () => void;
  onCustomize: () => void;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-8 p-4">
      <RogoDefaultCard
        selectedCount={props.selectedCount}
        busy={props.busy}
        rogoBlocked={props.rogoBlocked}
        unpriced={props.unpriced}
        onDownload={props.onRogo}
      />
      <BrandedReportCard
        selectedCount={props.selectedCount}
        busy={props.busy}
        branding={props.branding}
        brandingLoaded={props.brandingLoaded}
        showCustomize={props.showCustomize}
        customizeDisabled={props.customizeDisabled}
        onDownload={props.onBranded}
        onCustomize={props.onCustomize}
      />
    </div>
  );
}
